import React, { useEffect, useState } from "react";
import { getQualityGrid, getTiersQuestionnaire, saveTiersQuestionnaire } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { ClipboardList, Loader2, Save, ChevronDown, ChevronUp } from "lucide-react";
import { log } from "../../../lib/log";

const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand bg-white";

const Question = ({ q, value, onChange }) => {
  if (q.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`tq-${q.key}`}>
        {q.options.map((o) => { const on = arr.includes(o); return (
          <button key={o} type="button" onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])} className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-brand text-white border-brand" : "border-neutral-300 text-neutral-700 hover:border-brand"}`}>{o}</button>); })}
      </div>
    );
  }
  if (q.type === "select") return <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`tq-${q.key}`}><option value="">—</option>{q.options.map((o) => <option key={o}>{o}</option>)}</select>;
  if (q.type === "textarea") return <textarea rows={2} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`tq-${q.key}`} />;
  return <input type={q.type === "number" ? "number" : "text"} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`tq-${q.key}`} />;
};

/* « Services demandés » questionnaire stored per Dolibarr third party (socid). */
const TiersQuestionnaire = ({ socid }) => {
  const [schema, setSchema] = useState([]);
  const [answers, setAnswers] = useState({});
  const [meta, setMeta] = useState({});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    Promise.all([getQualityGrid(), getTiersQuestionnaire(socid)]).then(([g, q]) => { setSchema(g.data.questionnaire); setAnswers(q.data.answers || {}); setMeta(q.data); setOpen(!q.data.updated_at); }).catch(log.error);
  }, [socid]);

  const filled = Object.values(answers).filter((v) => (Array.isArray(v) ? v.length : v !== "" && v != null)).length;
  const save = async () => {
    setBusy(true);
    try { const { data } = await saveTiersQuestionnaire(socid, answers); setMeta(data); toast({ title: "Questionnaire enregistré ✓" }); }
    catch (e) { toast({ title: "Erreur", description: e?.response?.data?.detail || "Enregistrement impossible", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 mt-4" data-testid="tiers-questionnaire">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="tiers-questionnaire-toggle">
        <span className="flex items-center gap-2 font-semibold text-neutral-900"><ClipboardList size={17} className="text-brand" /> Questionnaire — services demandés
          <span className="text-xs font-normal text-neutral-500">{filled}/{schema.length} réponses{meta.updated_at && ` · mis à jour le ${new Date(meta.updated_at).toLocaleDateString("fr-FR")}${meta.updated_by ? ` par ${meta.updated_by}` : ""}`}</span></span>
        {open ? <ChevronUp size={16} className="text-neutral-400" /> : <ChevronDown size={16} className="text-neutral-400" />}
      </button>
      {open && (
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          {schema.map((q) => (
            <div key={q.key} className={q.type === "multi" || q.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className="block text-xs font-semibold text-neutral-600 mb-1">{q.label}</label>
              <Question q={q} value={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} />
            </div>
          ))}
          <div className="sm:col-span-2 flex justify-end">
            <button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark text-white font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60" data-testid="tiers-questionnaire-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiersQuestionnaire;
